import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useApi, useWorkspace, nzDate, nzDateTime, NZD, num, label, monthLabel } from "@/lib/api";
import { PageHeader, Section, StatusBadge, Empty, Modal, Bar, Pill, Sel, useLoad } from "@/components/app/common";
import { ThisWeek } from "@/pages/ThisWeek";
import { Welcome } from "@/pages/Welcome";
import { ChecklistScores } from "@/pages/ChecklistScores";
import { ChatGPTConnector } from "@/components/app/ChatGPTConnector";
import { OverdueChecks, ScheduledChecks } from "@/components/app/PolicyChecks";

export function CausesModal({ status, open, onClose, title }) {
  return (
    <Modal open={open} onClose={onClose} title={title || "Housekeeping status"} wide>
      {status && (
        <div className="space-y-4" data-testid="status-causes">
          <div className="flex items-center gap-3"><StatusBadge level={status.level} /><span className="text-xs text-slate-500">Calculated {nzDateTime(status.calculated_at)}</span></div>
          {status.causes.length === 0 ? <p className="text-sm text-slate-600">All enabled mandatory requirements are current.</p> : (
            <ul className="divide-y divide-slate-100">
              {status.causes.map((c, i) => (
                <li key={i} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between" data-testid={`status-cause-${i}`}>
                  <div>
                    <p className="text-sm font-medium"><span className={`inline-block h-2 w-2 rounded-full mr-2 ${c.level === "red" ? "bg-red-500" : "bg-orange-500"}`} />{c.title}</p>
                    <p className="text-xs text-slate-500 ml-4">{c.category} · Responsible: <b>{c.responsible_name}</b>{c.due_date && ` · Original due ${nzDate(c.due_date)}`}</p>
                  </div>
                  <Link to={c.link} onClick={onClose} className="text-sm font-semibold inline-flex items-center gap-1 hover:underline shrink-0">Go to action<ArrowRight size={14} /></Link>
                </li>))}
            </ul>)}
        </div>)}
    </Modal>
  );
}

function GroupDashboard() {
  const api = useApi();
  const { groupId, setFarmId } = useWorkspace();
  const [data] = useLoad(() => api.get(`/dashboard/group?group_id=${groupId || "all"}`), [groupId]);
  const [f, setF] = useState({ level: "", arrangement: "", person: "" });
  const [show, setShow] = useState(null);
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const people = [...new Set(data.farms.flatMap((r) => r.responsible))];
  const rows = data.farms.filter((r) => (!f.level || r.level === f.level) && (!f.arrangement || r.arrangement === f.arrangement) && (!f.person || r.responsible.includes(f.person)));
  return (
    <>
      <Welcome groupId={groupId} />
      <PageHeader title="Farms overview" subtitle={`Scope: ${data.scope}. One row per farm; click a status to see each cause.`} />
      <ThisWeek groupId={groupId} />
      <OverdueChecks groupId={groupId || "all"} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Sel testid="filter-status" value={f.level} onChange={(v) => setF({ ...f, level: v })} placeholder="All statuses" options={[{ value: "red", label: "Action required" }, { value: "orange", label: "Due soon" }, { value: "green", label: "Current" }]} />
        <Sel testid="filter-arrangement" value={f.arrangement} onChange={(v) => setF({ ...f, arrangement: v })} placeholder="All arrangements" options={["owner_operated", "contract_milked", "sharemilking_50_50", "other"].map((v) => ({ value: v, label: label(v) }))} />
        <Sel testid="filter-person" value={f.person} onChange={(v) => setF({ ...f, person: v })} placeholder="Any responsible person" options={people} />
      </div>
      <div className="panel divide-y divide-slate-100" data-testid="group-farm-list">
        {rows.length === 0 && <Empty>No farms match. Administrators can add farms in Settings.</Empty>}
        {rows.map((r) => (
          <div key={r.farm_id} className="p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between row-hover" data-testid={`farm-row-${r.farm_id}`}>
            <div className="min-w-0">
              <button className="font-semibold text-left hover:underline" onClick={() => setFarmId(r.farm_id)} data-testid={`open-farm-${r.farm_id}`}>{r.name}</button>
              <p className="text-xs text-slate-500 mt-1">{r.group_name} · {label(r.arrangement)} · TCG: {label(r.tcg_role)}{r.halter ? " · Halter" : ""} {r.demo && <Pill tone="blue">Demo data</Pill>}</p>
              {r.causes[0] && <p className="text-xs text-slate-600 mt-1">{r.causes[0].title}{r.cause_count > 1 ? ` +${r.cause_count - 1} more` : ""}</p>}
            </div>
            <StatusBadge level={r.level} onClick={() => setShow(r)} testid={`farm-status-${r.farm_id}`} />
          </div>))}
      </div>
      <ChecklistScores groupId={groupId} />
      <CausesModal open={!!show} onClose={() => setShow(null)} title={show?.name} status={show && { level: show.level, causes: show.causes, calculated_at: new Date().toISOString() }} />
    </>
  );
}

const Row = ({ children, to, testid }) => (
  <li><Link to={to} className="flex items-center justify-between gap-3 py-3 row-hover px-2 -mx-2 rounded" data-testid={testid}>{children}</Link></li>
);

function FarmDashboard() {
  const api = useApi();
  const { farm } = useWorkspace();
  const [d] = useLoad(() => api.get(`/dashboard/farm?farm_id=${farm.id}`), [farm.id]);
  const [open, setOpen] = useState(false);
  const [perCow, setPerCow] = useState(false);
  if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
  const st = d.status;
  return (
    <>
      <Welcome farmId={farm.id} />
      <PageHeader title={farm.name} subtitle={`${label(farm.arrangement)} · TCG role: ${label(farm.tcg_role)}${farm.halter ? " · Halter farm" : ""}`}
        actions={<StatusBadge level={st.level} onClick={() => setOpen(true)} testid="farm-status-badge" />} />
      {st.causes.length > 0 && (
        <button onClick={() => setOpen(true)} className={`w-full text-left mb-6 panel p-4 border-l-4 ${st.level === "red" ? "border-l-red-500" : "border-l-orange-500"}`} data-testid="attention-banner">
          <p className="text-sm font-semibold">{st.causes.length} item(s) need attention</p>
          <p className="text-xs text-slate-600 mt-1">{st.causes.slice(0, 3).map((c) => c.title).join(" · ")}</p>
        </button>)}
      <ThisWeek farmId={farm.id} />
      <OverdueChecks farmId={farm.id} />
      <ScheduledChecks farmId={farm.id} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="My actions" testid="dash-my-tasks">
          {d.my_tasks.length === 0 ? <Empty>No open tasks assigned to you.</Empty> : (
            <ul>{d.my_tasks.map((t) => <Row key={t.id} to={`/tasks?id=${t.id}`} testid={`dash-task-${t.id}`}><span className="text-sm">{t.title}</span><span className="text-xs text-slate-500">{t.due_date ? nzDate(t.due_date) : "No date"}</span></Row>)}</ul>)}
        </Section>
        <Section title="Orders & spending needing a decision" testid="dash-decisions">
          {d.decisions.length === 0 ? <Empty>Nothing waiting for your decision.</Empty> : (
            <ul>{d.decisions.map((r) => <Row key={r.id} to={`/tasks?tab=requests&id=${r.id}`} testid={`dash-request-${r.id}`}><span className="text-sm">{r.description}</span><span className="text-xs font-mono-num">{NZD(r.est_cost)}</span></Row>)}</ul>)}
        </Section>
        <Section title="Monthly reports" testid="dash-reports">
          <ul>{d.reports.map((r) => <Row key={r.month} to={`/reporting?month=${r.month}`} testid={`dash-report-${r.month}`}><span className="text-sm">{monthLabel(r.month)}</span><span className="text-xs"><Pill tone={r.status === "reviewed" ? "green" : r.status === "submitted" ? "blue" : r.status === "returned" ? "red" : "slate"}>{label(r.status)}</Pill> due {nzDateTime(r.due_at)}</span></Row>)}</ul>
        </Section>
        <Section title="Latest production" testid="dash-production">
          {d.production ? (
            <div className="grid grid-cols-3 gap-4">
              <div><p className="label-sm">{monthLabel(d.production.month)}</p><p className="text-2xl font-bold font-mono-num">{num(d.production.kgms, 0)}</p><p className="text-xs text-slate-500">kgMS</p></div>
              <div><p className="label-sm">Target</p><p className="text-2xl font-bold font-mono-num">{num(d.production.target_kgms, 0)}</p><p className="text-xs text-slate-500">kgMS</p></div>
              <div><p className="label-sm">SCC</p><p className="text-2xl font-bold font-mono-num">{num(d.production.scc, 0)}</p><p className="text-xs text-slate-500">000/ml</p></div>
            </div>) : <Empty>No production figures entered yet. Add them through Reporting.</Empty>}
        </Section>
        {d.budget && (
          <Section title="Budget" testid="dash-budget" actions={d.budget.budget_cows ? <button className="text-xs underline" onClick={() => setPerCow(!perCow)} data-testid="dash-budget-toggle">{perCow ? "Show total NZD" : "Show budget per cow"}</button> : null}>
            <p className="text-xs text-slate-500 mb-3">Actuals updated through <b>{d.budget.actuals_through ? monthLabel(d.budget.actuals_through) : "not yet entered"}</b></p>
            {d.budget.categories.length === 0 ? <Empty>No budget categories yet.</Empty> : d.budget.categories.map((c) => (
              <div key={c.name} className="mb-3">
                <div className="flex justify-between text-sm mb-1"><span>{c.name}</span><span className="font-mono-num text-xs">{perCow ? `${NZD(c.actual_per_cow, 2)} / ${NZD(c.allowance_per_cow, 2)}` : `${NZD(c.actual)} / ${NZD(c.allowance)}`} · {c.pct_used ?? "—"}%</span></div>
                <Bar pct={c.pct_used} />
              </div>))}
          </Section>)}
        <Section title="Services due" testid="dash-services">
          {d.services.length === 0 ? <Empty>No services due soon.</Empty> : (
            <ul>{d.services.map((s, i) => <Row key={i} to={`/machinery?id=${s.asset_id}`} testid={`dash-service-${i}`}><span className="text-sm">{s.asset} — {s.schedule}{!s.mandatory && <span className="text-xs text-slate-400"> (optional)</span>}</span><Pill tone={s.status === "due_soon" ? "orange" : "red"}>{s.units_remaining != null ? `${s.units_remaining} remaining` : label(s.status)}</Pill></Row>)}</ul>)}
        </Section>
        <Section title="Inspection follow-ups" testid="dash-followups">
          {d.followups.length === 0 ? <Empty>No open follow-up jobs.</Empty> : <ul>{d.followups.map((t) => <Row key={t.id} to={`/tasks?id=${t.id}`} testid={`dash-followup-${t.id}`}><span className="text-sm">{t.title}</span><Pill>{label(t.status)}</Pill></Row>)}</ul>}
        </Section>
        <Section title="Notices" testid="dash-notices">
          {d.notices.length === 0 ? <Empty>No unread notices.</Empty> : <ul>{d.notices.map((n) => <Row key={n.id} to="/compliance?tab=notices" testid={`dash-notice-${n.id}`}><span className="text-sm">{n.title}</span>{n.requires_ack && <Pill tone="orange">Needs Got it</Pill>}</Row>)}</ul>}
        </Section>
        <Section title="Recent activity" testid="dash-activity">
          {d.activity.length === 0 ? <Empty>No activity yet.</Empty> : (
            <ul className="space-y-2">{d.activity.map((a) => <li key={a.id} className="text-xs text-slate-600"><b>{a.user_name}</b> {a.action.replace(/_/g, " ")} {a.entity.replace(/_/g, " ")} · {nzDateTime(a.at)}</li>)}</ul>)}
        </Section>
      </div>
      <CausesModal open={open} onClose={() => setOpen(false)} status={st} title={`${farm.name} — housekeeping status`} />
    </>
  );
}

export default function Dashboard() {
  const { farm } = useWorkspace();
  return (
    <>
      {farm ? <FarmDashboard /> : <GroupDashboard />}
      <footer className="mt-10 pt-6 border-t border-slate-200" data-testid="dashboard-footer"><ChatGPTConnector /></footer>
    </>
  );
}
